import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, Users, Wrench, ClipboardList, BarChart3, LogOut, Settings as SettingsIcon, Wallet, Menu, X, CalendarOff, ArrowLeft, Banknote, Calculator, Clapperboard, MessageSquare, Navigation, MessageCircle, Zap, TrendingUp } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const items = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/orders", label: "Order", icon: ClipboardList, testid: "nav-orders" },
  { to: "/customers", label: "Pelanggan", icon: Users, testid: "nav-customers" },
  { to: "/leaves", label: "Libur Karyawan", icon: CalendarOff, testid: "nav-leaves" },
  { to: "/expenses", label: "Pengeluaran", icon: Wallet, testid: "nav-expenses" },
  { to: "/sosmed", label: "Content Sosmed", icon: Clapperboard, testid: "nav-sosmed" },
  { to: "/jarak-tempuh", label: "Jarak Tempuh", icon: Navigation, testid: "nav-mileage" },
  { to: "/whatsapp", label: "WhatsApp", icon: MessageCircle, end: true, testid: "nav-whatsapp" },
  { to: "/whatsapp/shortcuts", label: "Shortcut Pesan", icon: Zap, testid: "nav-wa-shortcuts", child: true },
  { to: "/pesan", label: "Pesan Karyawan", icon: MessageSquare, testid: "nav-pesan" },
  { to: "/reports", label: "Laporan", icon: BarChart3, testid: "nav-reports" },
  { to: "/settings", label: "Pengaturan", icon: SettingsIcon, testid: "nav-settings" },
  { to: "/technicians", label: "Karyawan", icon: Wrench, testid: "nav-technicians", child: true },
  { to: "/kasbon", label: "Kasbon Karyawan", icon: Banknote, testid: "nav-kasbon", child: true },
  { to: "/payroll", label: "Payroll", icon: Calculator, testid: "nav-payroll", child: true },
  { to: "/laporan-keuangan", label: "Laporan Keuangan", icon: TrendingUp, testid: "nav-finance", child: true },
];

const Layout = ({ children }) => {
  const { logout, user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === "/" || location.pathname === "/dashboard";
  // Initial state pakai cached brand dari localStorage supaya tidak flicker saat navigasi antar halaman
  const [brand, setBrand] = useState(() => {
    try {
      const cached = localStorage.getItem("om_brand_cache");
      if (cached) return JSON.parse(cached);
    } catch (_) { /* noop */ }
    return { company_name: "", logo_base64: "" };
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Set title immediately from cached brand supaya tidak flicker ORDER.OS ↔ nama company
  useEffect(() => {
    const name = (brand.company_name || "").trim();
    document.title = name || "ORDER.OS";
  }, [brand.company_name]);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      const data = r.data || {};
      setBrand(data);
      try { localStorage.setItem("om_brand_cache", JSON.stringify(data)); } catch (_) { /* noop */ }
    }).catch(() => {});
  }, []);

  const [waUnread, setWaUnread] = useState(0);
  useEffect(() => {
    const fetchUnread = () => api.get("/wa/unread").then((r) => setWaUnread(r.data?.count || 0)).catch(() => {});
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, []);

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  const companyName = (brand.company_name || "").trim() || "ORDER.OS";
  const logo = (brand.logo_base64 || "").trim();

  const BrandBlock = () => (
    <div className="px-4 sm:px-6 py-5 border-b border-white/10" data-testid="sidebar-brand">
      {logo ? (
        <div className="flex items-center gap-3">
          <img src={logo} alt={companyName} className="h-10 w-10 object-contain bg-white p-1 shrink-0" data-testid="sidebar-brand-logo" />
          <div className="flex-1 min-w-0">
            <div className="font-heading text-sm font-black tracking-tight leading-tight line-clamp-2 break-words">{companyName}</div>
            <div className="label-cap text-white/50 text-[9px] mt-1">Manajemen Operasi</div>
          </div>
        </div>
      ) : (
        <>
          <div className="font-heading text-xl font-black tracking-tight leading-tight break-words">{companyName}</div>
          <div className="label-cap text-white/50 mt-1 text-[10px]">Manajemen Operasi</div>
        </>
      )}
    </div>
  );

  const NavList = ({ onClick }) => (
    <nav className="flex-1 py-4">
      {items.map((it) => (
        <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          data-testid={it.testid}
          onClick={onClick}
          className={({ isActive }) =>
            `flex items-center gap-3 ${it.child ? "pl-12 pr-6" : "px-6"} py-3 text-sm transition-colors min-h-[44px] ${
              isActive ? "bg-white text-[#111113] font-bold" : "text-white/80 hover:bg-white/5 hover:text-white"
            }`
          }
        >
          <it.icon size={it.child ? 14 : 16} /> {it.label}
          {it.to === "/whatsapp" && waUnread > 0 && (
            <span data-testid="nav-whatsapp-badge" className="ml-auto bg-[#16A34A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">{waUnread}</span>
          )}
        </NavLink>
      ))}
    </nav>
  );

  const Footer = () => (
    <div className="px-6 py-4 border-t border-white/10">
      <div className="text-xs text-white/50 mb-2 truncate" data-testid="sidebar-user-email">{user?.email}</div>
      <button onClick={handleLogout} data-testid="logout-button" className="flex items-center gap-2 text-xs text-white/80 hover:text-white min-h-[44px]">
        <LogOut size={14} /> Keluar
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Mobile top bar */}
      <header className="md:hidden fixed top-0 inset-x-0 h-14 bg-white border-b border-[#E4E4E7] z-40 flex items-center px-3 gap-2" data-testid="mobile-topbar">
        <button onClick={() => setDrawerOpen(true)} data-testid="mobile-menu-button" className="p-2 -ml-1 hover:bg-[#F4F4F5] shrink-0" aria-label="Buka menu">
          <Menu size={22} />
        </button>
        {logo ? (
          <img src={logo} alt={companyName} className="h-7 w-7 object-contain shrink-0" />
        ) : null}
        <div className="font-heading font-black text-sm tracking-tight truncate flex-1 min-w-0">{companyName}</div>
      </header>

      {/* Mobile drawer */}
      {drawerOpen && (
        <>
          <div className="md:hidden fixed inset-0 bg-black/40 z-50" onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-overlay" />
          <aside className="md:hidden fixed inset-y-0 left-0 w-[260px] max-w-[80vw] bg-[#111113] text-white z-50 flex flex-col" data-testid="mobile-drawer">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="label-cap text-white/50">Menu</div>
              <button onClick={() => setDrawerOpen(false)} data-testid="mobile-drawer-close" className="p-2 hover:bg-white/5"><X size={20} /></button>
            </div>
            <BrandBlock />
            <NavList onClick={() => setDrawerOpen(false)} />
            <Footer />
          </aside>
        </>
      )}

      {/* Desktop sidebar */}
      <aside className="hidden md:flex w-60 bg-[#111113] text-white fixed inset-y-0 left-0 flex-col" data-testid="app-sidebar">
        <BrandBlock />
        <NavList />
        <Footer />
      </aside>

      {/* Main content */}
      <main className="md:ml-60 pt-14 md:pt-0 min-h-screen">
        {!isDashboard && (
          <div className="px-3 sm:px-6 lg:px-10 pt-3 sm:pt-6 lg:pt-10">
            <button
              onClick={() => (window.history.length > 1 ? nav(-1) : nav("/"))}
              data-testid="back-button"
              className="inline-flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:bg-white border border-[#E4E4E7] bg-white/60"
            >
              <ArrowLeft size={14} /> Kembali
            </button>
          </div>
        )}
        <div className={`p-3 sm:p-6 lg:p-10 ${!isDashboard ? "pt-3 sm:pt-4 lg:pt-6" : ""}`}>{children}</div>
      </main>
    </div>
  );
};

export default Layout;
